import type { ReactNode } from 'react';

import { cn } from '@/services/utils';

// Mirrors the placeholders accepted by SlottedFormGuard, including typographic-apostrophe variants
// retained by legacy cards. Safe to match by word alone here only because the caller gates on
// `active` (DTO's `isSlotted`) — the server already marked this specific string as a slotted form,
// so any of these words in it really is a placeholder, not a coincidence like "one" in
// "kill two birds with one stone".
const SLOT_WORDS = new Set([
  'someone',
  "someone's",
  'someone’s',
  'something',
  "one's",
  'one’s',
  'one',
  'oneself',
  'somewhere',
]);

// Dictionary-style short forms, so a slot costs a phrase title a couple of characters instead of
// nine. Display only — the stored phrase keeps the full words, which is what edit, search and
// text-to-speech read. The remaining placeholders are already short and stay as they are.
const SLOT_ABBREVIATIONS: Record<string, string> = {
  someone: 'sb',
  "someone's": "sb's",
  'someone’s': "sb's",
  something: 'smth',
};

type SlotToken = { prefix: string; word: string; suffix: string };

// Splits surrounding punctuation off the word so it stays outside the placeholder and does not
// defeat the abbreviation lookup.
const readSlotToken = (token: string): SlotToken | null => {
  const word = token.replace(/^[^\w']+|[^\w']+$/gu, '');
  if (word.length === 0 || !SLOT_WORDS.has(word.toLowerCase())) {
    return null;
  }

  const start = token.indexOf(word);
  return { prefix: token.slice(0, start), word, suffix: token.slice(start + word.length) };
};

const abbreviate = (word: string) => SLOT_ABBREVIATIONS[word.toLowerCase()] ?? word;

type HighlightedSlotsProps = {
  text: string;
  active: boolean;
  className?: string;
};

export function HighlightedSlots({ text, active, className }: HighlightedSlotsProps) {
  if (!active) {
    return <>{text}</>;
  }

  const tokens = text.split(/(\s+)/);
  const parts: ReactNode[] = tokens.map((token, index) => {
    const slot = readSlotToken(token);

    return slot ? (
      <span key={index} className={cn('font-normal text-slate-400', className)}>
        {slot.prefix}
        {abbreviate(slot.word)}
        {slot.suffix}
      </span>
    ) : (
      <span key={index}>{token}</span>
    );
  });

  return <>{parts}</>;
}
